import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  Filter,
  Eye,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Paperclip,
  X,
  User,
  Mail,
  Phone,
  ShoppingBag,
  Send,
  Loader2,
  Maximize2,
  RefreshCw,
} from "lucide-react";
import { QueryStatus, QuerySubject } from "../../../types/Response/CustomerResponse";
import {
  useLazyGetQueryDetailsQuery,
  useLazyGetQueryListQuery,
  useUpdateQueryMutation,
} from "../../../redux/actions/Admin/footerManagementAdmin";
import { ICustomerQuery } from "../../../types/Response/AdminResponse";
import { IUpdateCustomerQueryRequest } from "../../../types/Request/AdminRequest";

// Safe status parsing helper to handle string names or numeric enum values
const parseQueryStatus = (status: string | number): QueryStatus => {
  if (typeof status === "number") return status;
  const s = String(status || "").toLowerCase().trim();
  if (s === "pending" || s === "0") return QueryStatus.Pending;
  if (s === "inprogress" || s === "in_progress" || s === "1") return QueryStatus.InProgress;
  if (s === "resolved" || s === "2") return QueryStatus.Resolved;
  if (s === "rejected" || s === "closed" || s === "3") return QueryStatus.Closed;
  return QueryStatus.Pending;
};

// Helper for status badges using brand-aligned color accents
const getStatusBadge = (status: string | number) => {
  const numericStatus = parseQueryStatus(status);
  switch (numericStatus) {
    case QueryStatus.Pending:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
          <Clock className="w-3.5 h-3.5 text-amber-500" /> Pending
        </span>
      );
    case QueryStatus.InProgress:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
          <AlertCircle className="w-3.5 h-3.5 text-blue-500" /> In Progress
        </span>
      );
    case QueryStatus.Resolved:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Resolved
        </span>
      );
    case QueryStatus.Closed:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
          <XCircle className="w-3.5 h-3.5 text-rose-500" /> Closed
        </span>
      );
  }
};

const getSubjectLabel = (subject: string | number) => {
  if (typeof subject === "number") {
    switch (subject) {
      case QuerySubject.OrderSupport:
        return "Order Issue";
      case QuerySubject.GeneralInquiry:
        return "General Inquiry";
      case QuerySubject.PetTaxi:
        return "Pet Taxi";
      case QuerySubject.GroomingServices:
        return "Grooming Service";
      case QuerySubject.VendorPartnership:
        return "Vendor / Partnership";
      default:
        return "General";
    }
  }
  return subject || "General";
};

export const AdminQueriesScreen: React.FC = () => {
  // RTK Queries & Mutations
  const [
    triggerGetQueryList,
    { data: queryList, isLoading: isLoadingList, isFetching: isFetchingList },
  ] = useLazyGetQueryListQuery();
  const [
    triggerGetQueryDetails,
    { data: queryDetails, isLoading: isLoadingDetails },
  ] = useLazyGetQueryDetailsQuery();
  const [updateQuery, { isLoading: isUpdating }] = useUpdateQueryMutation();

  // Selected Query ID
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null);

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [subjectFilter, setSubjectFilter] = useState<string>("ALL");

  // Lightbox Preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Form State for Responding
  const [respondForm, setRespondForm] = useState<{
    status: QueryStatus;
    adminComment: string;
    adminResponse: string;
  }>({
    status: QueryStatus.Resolved,
    adminComment: "",
    adminResponse: "",
  });

  // Initial Fetch
  useEffect(() => {
    triggerGetQueryList();
  }, [triggerGetQueryList]);

  // Sync query details response into form state when drawer opens
  useEffect(() => {
    if (queryDetails) {
      const currentStatus = parseQueryStatus(queryDetails.status);
      setRespondForm({
        status: currentStatus === QueryStatus.Pending ? QueryStatus.Resolved : currentStatus,
        adminComment: queryDetails.adminComment || "",
        adminResponse: queryDetails.adminResponse || "",
      });
    }
  }, [queryDetails]);

  // Open Details Drawer
  const handleViewDetails = (queryId: number) => {
    setSelectedQueryId(queryId);
    triggerGetQueryDetails(queryId);
  };

  // Submit Response Handler
  const handleSubmitResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQueryId) return;

    const payload: IUpdateCustomerQueryRequest = {
      queryId: selectedQueryId,
      status: Number(respondForm.status),
      adminComment: respondForm.adminComment,
      adminResponse: respondForm.adminResponse,
    };

    try {
      await updateQuery(payload).unwrap();
      // Refresh details and list
      await triggerGetQueryDetails(selectedQueryId);
      await triggerGetQueryList();
    } catch (error) {
      console.error("Failed to update query response:", error);
    }
  };

  const queriesList: ICustomerQuery[] = Array.isArray(queryList)
    ? queryList
    : (queryList as any)?.data || [];

  // Filtered queries list logic
  const filteredQueries = useMemo(() => {
    return queriesList.filter((q) => {
      const matchesSearch =
        q.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (q.orderNumber && q.orderNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
        q.id.toString().includes(searchQuery);

      const parsedStatus = parseQueryStatus(q.status);
      const matchesStatus =
        statusFilter === "ALL" || parsedStatus.toString() === statusFilter;

      const subjectLabel = getSubjectLabel(q.subject).toLowerCase();
      const matchesSubject =
        subjectFilter === "ALL" ||
        subjectLabel.includes(subjectFilter.toLowerCase());

      return matchesSearch && matchesStatus && matchesSubject;
    });
  }, [queriesList, searchQuery, statusFilter, subjectFilter]);

  return (
    <div className="max-w-7xl mx-auto p-6 text-left">
      {/* Header Section */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
            Customer Queries
          </h1>
          <p className="text-xs text-slate-500 font-semibold mt-1">
            Manage customer support tickets, review attached media, and dispatch responses.
          </p>
        </div>

        <button
          onClick={() => triggerGetQueryList()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-50 transition shadow-2xs cursor-pointer self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetchingList ? "animate-spin text-[#6DBE45]" : "text-[#8B6B3E]"}`} />
          Refresh List
        </button>
      </div>

      {/* Filter and Search Controls */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        {/* Search Input */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3.5 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email, order #..."
            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:bg-white focus:outline-none focus:border-[#6DBE45] focus:ring-2 focus:ring-[#6DBE45]/20 transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Dropdown Filters */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-[#6DBE45]"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value={QueryStatus.Pending.toString()}>Pending</option>
              <option value={QueryStatus.InProgress.toString()}>In Progress</option>
              <option value={QueryStatus.Resolved.toString()}>Resolved</option>
              <option value={QueryStatus.Closed.toString()}>Closed</option>
            </select>
          </div>

          <select
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-[#6DBE45]"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            <option value="ALL">All Subjects</option>
            <option value="General">General Inquiry</option>
            <option value="Order Issue">Order Issue</option>
            <option value="Pet Taxi">Pet Taxi</option>
            <option value="Grooming">Grooming Service</option>
            <option value="Vendor">Vendor / Partnership</option>
          </select>
        </div>
      </div>

      {/* Query List Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100 text-[11px] uppercase tracking-wider font-extrabold text-slate-400">
                <th className="py-3.5 px-4">Query ID</th>
                <th className="py-3.5 px-4">Customer</th>
                <th className="py-3.5 px-4">Subject</th>
                <th className="py-3.5 px-4">Order Ref</th>
                <th className="py-3.5 px-4">Created On</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
              {isLoadingList ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400 font-bold">
                    <Loader2 className="w-6 h-6 animate-spin text-[#6DBE45] mx-auto mb-2" />
                    Loading queries...
                  </td>
                </tr>
              ) : filteredQueries.length > 0 ? (
                filteredQueries.map((query) => (
                  <tr key={query.id} className="hover:bg-emerald-50/30 transition-colors">
                    <td className="py-3.5 px-4 font-black text-slate-900">#{query.id}</td>
                    <td className="py-3.5 px-4">
                      <p className="font-bold text-slate-800">{query.name}</p>
                      <p className="text-[11px] text-slate-400">{query.email}</p>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="font-bold text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200/60">
                        {getSubjectLabel(query.subject)}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      {query.orderNumber ? (
                        <span className="font-mono text-xs font-bold text-[#2F5E2E]">
                          {query.orderNumber}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-slate-500">
                      {new Date(query.createdOn).toLocaleDateString()}
                    </td>
                    <td className="py-3.5 px-4">{getStatusBadge(query.status)}</td>
                    <td className="py-3.5 px-4 text-right">
                      <button
                        onClick={() => handleViewDetails(query.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-[#2F5E2E] hover:bg-emerald-100 font-bold rounded-xl text-xs transition border border-emerald-200 cursor-pointer"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-400 font-medium">
                    No customer queries match your search or filter settings.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details & Response Drawer */}
      {selectedQueryId && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
          <div className="w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col justify-between overflow-y-auto animate-in slide-in-from-right duration-300">
            {isLoadingDetails ? (
              <div className="flex-1 flex flex-col items-center justify-center space-y-2">
                <Loader2 className="w-8 h-8 text-[#6DBE45] animate-spin" />
                <p className="text-xs font-bold text-slate-400">Loading query details...</p>
              </div>
            ) : queryDetails ? (
              <>
                <div>
                  {/* Drawer Header */}
                  <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-2.5">
                      <span className="font-black text-lg text-slate-900">
                        Query #{queryDetails.id}
                      </span>
                      {getStatusBadge(queryDetails.status)}
                    </div>
                    <button
                      onClick={() => setSelectedQueryId(null)}
                      className="p-1.5 rounded-full hover:bg-slate-100 text-slate-400 transition"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Customer Details Summary Box */}
                    <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 grid grid-cols-2 gap-3 text-xs">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#8B6B3E] shrink-0" />
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-bold block">
                            Name
                          </span>
                          <span className="font-bold text-slate-800">
                            {queryDetails.name}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#8B6B3E] shrink-0" />
                        <div className="truncate">
                          <span className="text-[10px] text-slate-400 uppercase font-bold block">
                            Email
                          </span>
                          <span className="font-bold text-slate-800 truncate">
                            {queryDetails.email}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-[#8B6B3E] shrink-0" />
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-bold block">
                            Phone
                          </span>
                          <span className="font-bold text-slate-800">
                            {queryDetails.phone || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4 text-[#8B6B3E] shrink-0" />
                        <div>
                          <span className="text-[10px] text-slate-400 uppercase font-bold block">
                            Order Number
                          </span>
                          <span className="font-bold text-slate-800">
                            {queryDetails.orderNumber || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Customer Message Section */}
                    <div>
                      <h4 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider mb-2 flex items-center gap-1.5">
                        <MessageSquare className="w-4 h-4 text-[#2F5E2E]" /> Customer Message
                      </h4>
                      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 text-xs text-slate-800 leading-relaxed font-medium">
                        {queryDetails.message}
                      </div>
                    </div>

                    {/* Attachments Section */}
                    {queryDetails.attachments && queryDetails.attachments.length > 0 && (
                      <div>
                        <h4 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider mb-2 flex items-center gap-1.5">
                          <Paperclip className="w-4 h-4 text-[#2F5E2E]" /> Attachments ({queryDetails.attachments.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          {queryDetails.attachments.map((imgUrl, index) => (
                            <div
                              key={index}
                              className="relative group rounded-xl overflow-hidden border border-slate-200 aspect-square bg-slate-100"
                            >
                              <img
                                src={imgUrl}
                                alt={`Attachment ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                              <button
                                onClick={() => setPreviewImage(imgUrl)}
                                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                              >
                                <Maximize2 className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <hr className="border-slate-100" />

                    {/* Response & Action Form */}
                    <form onSubmit={handleSubmitResponse} className="space-y-4">
                      <h4 className="text-xs font-extrabold uppercase text-slate-400 tracking-wider">
                        Admin Action & Response
                      </h4>

                      {/* Status Selection Dropdown */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Update Status *
                        </label>
                        <select
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold bg-white text-slate-800 focus:outline-none focus:border-[#6DBE45]"
                          value={respondForm.status}
                          onChange={(e) =>
                            setRespondForm((prev) => ({
                              ...prev,
                              status: Number(e.target.value) as QueryStatus,
                            }))
                          }
                        >
                          <option value={QueryStatus.Pending}>Pending</option>
                          <option value={QueryStatus.InProgress}>In Progress</option>
                          <option value={QueryStatus.Resolved}>Resolved</option>
                          <option value={QueryStatus.Closed}>Closed</option>
                        </select>
                      </div>

                      {/* Internal Admin Note */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Internal Admin Notes <span className="text-slate-400 font-normal">(Private)</span>
                        </label>
                        <input
                          type="text"
                          className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white text-slate-800 focus:outline-none focus:border-[#6DBE45]"
                          placeholder="Optional internal note for team members..."
                          value={respondForm.adminComment}
                          onChange={(e) =>
                            setRespondForm((prev) => ({
                              ...prev,
                              adminComment: e.target.value,
                            }))
                          }
                        />
                      </div>

                      {/* Official Customer Response */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Official Customer Response <span className="text-slate-400 font-normal">(Emailed/Shown to user)</span>
                        </label>
                        <textarea
                          rows={4}
                          className="w-full border border-slate-200 rounded-xl p-3 text-xs font-medium bg-white text-slate-800 focus:outline-none focus:border-[#6DBE45]"
                          placeholder="Type response here..."
                          value={respondForm.adminResponse}
                          onChange={(e) =>
                            setRespondForm((prev) => ({
                              ...prev,
                              adminResponse: e.target.value,
                            }))
                          }
                        />
                      </div>

                      {/* Submit Actions */}
                      <div className="flex justify-end gap-3 pt-2">
                        <button
                          type="button"
                          onClick={() => setSelectedQueryId(null)}
                          className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isUpdating}
                          className="bg-[#6DBE45] hover:bg-[#5FB63F] text-white px-5 py-2 rounded-xl text-xs font-extrabold flex items-center gap-2 transition disabled:opacity-50 cursor-pointer shadow-sm"
                        >
                          {isUpdating ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Send className="w-3.5 h-3.5" />
                          )}
                          {isUpdating ? "Saving..." : "Submit Response"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Image Preview Lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="fixed top-6 right-6 z-[101] bg-white/20 hover:bg-white/40 text-white p-2.5 rounded-full transition-colors cursor-pointer border border-white/20"
            title="Close preview"
          >
            <X className="w-6 h-6" />
          </button>

          <div
            className="relative max-w-4xl max-h-[85vh] flex items-center justify-center overflow-hidden rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewImage}
              alt="Enlarged attachment preview"
              className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQueriesScreen;